import { useEffect, useContext, type JSX } from 'react';
import { gql } from '@apollo/client';
import { DocumentNode } from 'graphql';
import ServerError from '../../ServerError/ServerError';
import { fieldDefsToFields } from '../../../Utils/columnDefsToFields';
import { InternalError } from '../../InternalError/InternalError';
import { toString } from 'ramda';
import { Result } from 'true-myth';
import { useMemo } from 'react';
import { Query } from '@apollo/client/react/components';
import { IUIUrval } from '../../../Types/UrvalTypes';
import { tryConvertUIUrvalToTEDUrval } from '../../Urval/Utils/convertUIUrvalToTEDUrval';
import {
  findBusinessColors,
  findBusinessDataDependencies,
} from '../../../Features/Delsystem/_Common/Const/BusinessConfigs';
import { IClientBusinessModel } from '../../../Types/ClientModelTypes';
import { StandardRutinContext } from '../../../Features/Delsystem/_StandardRutin/StandardRutinContext';
import { NewId } from '../../MasterDetail/MasterDetailUtils';

export interface INodeData {
  id: string;
  _version?: number;
  _Associations?: { [index: string]: any } | null;
  [index: string]: any;
}

export interface IConnectedProps {
  id: string | null;
  index?: number;
}

interface IDetaljurval {
  /** Name of the association which the detaljurval should be added on */
  associationName: string;
  urval: IUIUrval | null;
}

interface IProps {
  collectionName: string;
  id: string | null;
  fieldDefs: string[];
  children: (
    loading: boolean,
    node: INodeData | null,
    refetch: () => void
  ) => JSX.Element | null;
  detaljurval?: IDetaljurval;
  associatedCollection?: IClientBusinessModel;
  caption?: string;
  skip?: boolean;
  /** If true, the query will not be automatically added to the refetch button queries. Add to IRoutine instead for better performance */
  disableAddRefetchButtonQuery?: boolean;
}

const nodeQueryOperationName = (collectionName: string) =>
  'node' + collectionName;

const nodeQuery = (
  collectionName: string,
  fieldDefs: string[],
  detaljurvalAssociationName?: string,
  associationCollection?: IClientBusinessModel
): string => {
  const listColors = findBusinessColors(
    associationCollection?.CollectionName || ''
  );
  const businessDataDependencies = findBusinessDataDependencies(
    associationCollection?.CollectionName || ''
  );
  const listColorDataDep =
    listColors
      ?.flatMap(color => color.dataDependencies)
      .map(x => `${detaljurvalAssociationName}.${x}`) ?? [];
  const detaljUrvalString = detaljurvalAssociationName
    ? `, $detaljurval: [[TEDInputSelectionCondition!]]`
    : '';
  return `query ${nodeQueryOperationName(
    collectionName
  )}($id: ID!${detaljUrvalString}) {
    node(id: $id) {
      ... on TED${collectionName} {
        ${fieldDefsToFields(
          collectionName,
          detaljurvalAssociationName
        )([...listColorDataDep, ...fieldDefs, ...businessDataDependencies])}
      }
    }
  }
  `;
};

// prettier-ignore
const errormessage = (querystring: string, error: string) => (
`Fel vid parsning av query:
${querystring}

Felmeddelande från parser:
${error}`);

export const makeNodeQuery = (
  collectionName: string,
  fieldDefs: string[],
  detaljurvalAssociationName?: string,
  associationCollection?: IClientBusinessModel,
  _caption?: string
): Result<DocumentNode, string> => {
  let querystring: string;
  querystring = nodeQuery(
    collectionName,
    fieldDefs,
    detaljurvalAssociationName,
    associationCollection
  );
  let query;
  try {
    query = gql(querystring);
  } catch (error) {
    return Result.err(errormessage(querystring, toString(error)));
  }
  return Result.ok(query);
};

export const NodeQuery = (props: IProps) => {
  const { disableAddRefetchButtonQuery } = props;
  const standardRutinContext = useContext(StandardRutinContext);
  const { addRefetchButtonQuery } = standardRutinContext;
  const query = useMemo(
    () =>
      makeNodeQuery(
        props.collectionName,
        props.fieldDefs,
        props.detaljurval?.associationName,
        props.associatedCollection,
        props.caption
      ),
    [
      props.collectionName,
      props.fieldDefs,
      props.detaljurval?.associationName,
      props.associatedCollection,
      props.caption,
    ]
  );
  useEffect(() => {
    if (!disableAddRefetchButtonQuery) {
      addRefetchButtonQuery(nodeQueryOperationName(props.collectionName));
    }
  }, [
    props.collectionName,
    disableAddRefetchButtonQuery,
    addRefetchButtonQuery,
  ]);
  const variables = useMemo(
    () => ({
      id: props.id,
      detaljurval: tryConvertUIUrvalToTEDUrval(props.detaljurval?.urval),
    }),
    [props.id, props.detaljurval?.urval]
  );
  if (query.isOk()) {
    return (
      <Query<any>
        query={query.value}
        skip={
          props.id === null ||
          props.id === '' ||
          props.id === NewId ||
          props.skip
        }
        variables={variables}
      >
        {({ loading, error, data, refetch }): JSX.Element | null => {
          const nodeData = dataAsNodeData(data);
          if (error) {
            return (
              <ServerError
                error={error}
                associationName={props.detaljurval?.associationName}
                caption={props.caption}
              />
            );
          }
          return props.children(loading, nodeData, refetch);
        }}
      </Query>
    );
  }

  return (
    <InternalError
      id="b994a824-9b5a-435a-b577-bc37ae74baf3"
      message={query.error}
    />
  );
};

/**Typar data eftersom vi vet att det alltid är en node vi frågar efter */
export const dataAsNodeData = (data: any): INodeData | null =>
  data === null || data === undefined || typeof data !== 'object'
    ? null
    : (data.node as INodeData);

export default NodeQuery;
